import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useCallback } from 'react';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { useActiveFusionWriter } from '~/hooks/target/useActiveFusionWriter';
import {
    domainEventBus,
    RegistrationCreateRequestedEvent,
} from '~/domain/events';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';

import useSelectedPatient from '../patient/useSelectedPatient';

/**
 * The secondary image of the primary row's fused pair, and the pick that
 * fuses one.
 */
export default function useSecondaryImage() {
    const patient = useSelectedPatient();
    const selectedSecondaryImage = useActiveFusionTarget().secondaryImage;
    const primaryImage = useSelectedPrimaryImage();
    const { fuse, clear } = useActiveFusionWriter();

    const handleChangeSecondaryImage = useCallback(
        (
            image: Image3D | null,
            registration?: RigidRegistration | undefined
        ) => {
            if (!primaryImage) {
                return;
            }
            // deselect: clear secondary image and registration
            if (!image) {
                clear(primaryImage);
                return;
            }

            if (!patient?.images?.includes(image)) {
                return;
            }

            const sharesFOR =
                primaryImage.frameOfReferenceUid === image.frameOfReferenceUid;

            if (registration) {
                fuse(primaryImage, image, registration);
            } else if (sharesFOR) {
                // Same Frame of Reference — already spatially aligned,
                // fuse directly without registration.
                fuse(primaryImage, image);
            } else {
                // No existing registration — request creation via
                // domain event. The handler creates it and leaves it
                // waiting for its automatic alignment; the pair is fused
                // when the wait ends.
                domainEventBus.publish(
                    new RegistrationCreateRequestedEvent(
                        primaryImage.seriesUid,
                        image.seriesUid
                    )
                );
            }
        },
        [patient, primaryImage, fuse, clear]
    );

    return {
        selectedSecondaryImage,
        handleChangeSecondaryImage,
    };
}
